import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getAds } from '../../../../../../modules/actions/ads'
import { adsSelector, adsWasLoadedSelector } from '../../../../../../modules/selectors/ads'
import { lineItemsSelector } from '../../../../../../modules/selectors/lineItems'

import { LANDING_PAGE_VIEWS, LINK_CLICKS, OUTCOME_TRAFFIC } from '../../../../../../constants/campaigns'
import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import {
  ARE_WE_ABLE_TO_TRACK_LANDING_PAGE_VIEWS,
  NO_I_HAVE_NOT_LINKED_BY_WEBSITE_PIXEL,
  YES_I_HAVE_LINKED_BY_WEBSITE_PIXEL
} from '../fields'
import { PROMOTED_PIXEL } from '../../../../../../constants/selectLists/customEventTypes/fields'

const defaultValues = {
  preselectedValues: {},
  showPromotedObjectStepForTrafficCampaign: true
}
export function useOutcomeTrafficCampaign({
  campaignId,
  facebookAdAccountId,
  campaignObjective,
  isFirstLineItemCreation
}) {
  const dispatch = useDispatch()

  const ads = useSelector(adsSelector)
  const lineItems = useSelector(lineItemsSelector)
  const adsWasLoaded = useSelector(adsWasLoadedSelector)

  const firstLineItem = isFirstLineItemCreation ? undefined : lineItems[0]
  const firstLineItemId = firstLineItem?.id

  const isFirstLineItemObjectiveLinkClicks = firstLineItem?.objective === LINK_CLICKS
  const isFirstLineItemObjectiveLandingPageViews = firstLineItem?.objective === LANDING_PAGE_VIEWS

  // skip ads loading if we are creating first line item, as there are no ads yet - in other case after first line item creation the ads become fetching, and isLoading set to true. This causes the onSuccessSubmit to skip LineItemForm.
  const shouldLoadAds =
    campaignObjective === OUTCOME_TRAFFIC && !isFirstLineItemCreation && isFirstLineItemObjectiveLandingPageViews

  const selectedPixelOnFirstAd = useMemo(
    () => ads[0]?.tracking_specs?.find(item => item.fb_pixel)?.fb_pixel?.[0],
    [ads]
  )

  const isDataLoading = shouldLoadAds ? !adsWasLoaded : false

  const getOutcomeTrafficValues = useCallback(() => {
    if (isFirstLineItemObjectiveLinkClicks) {
      // if first lineItem is optimised towards "link clicks"- we don't show pixel selection step,
      // and automatically select NO_I_HAVE_NOT_LINKED_BY_WEBSITE_PIXEL option
      return {
        showPromotedObjectStepForTrafficCampaign: false,
        preselectedValues: {
          [ARE_WE_ABLE_TO_TRACK_LANDING_PAGE_VIEWS]: NO_I_HAVE_NOT_LINKED_BY_WEBSITE_PIXEL
        }
      }
    } else if (isFirstLineItemObjectiveLandingPageViews && selectedPixelOnFirstAd) {
      // if first lineItems is optimised towards "landing page views" - we check the first ad in the campaign
      // if it exists, and it has pixel selected, we don't show pixel selection step,
      // and automatically select YES_I_HAVE_LINKED_BY_WEBSITE_PIXEL option, and use the pixel from the first ad
      return {
        showPromotedObjectStepForTrafficCampaign: false,
        preselectedValues: {
          [ARE_WE_ABLE_TO_TRACK_LANDING_PAGE_VIEWS]: YES_I_HAVE_LINKED_BY_WEBSITE_PIXEL,
          [PROMOTED_PIXEL]: selectedPixelOnFirstAd
        }
      }
    } else {
      return defaultValues
    }
  }, [isFirstLineItemObjectiveLandingPageViews, isFirstLineItemObjectiveLinkClicks, selectedPixelOnFirstAd])

  useEffect(() => {
    // If campaign objective is traffic and first line item objective is "landing page views"
    // we need to fetch ads to check if there is a pixel selected on the first ad
    if (shouldLoadAds) {
      dispatch(
        getAds(
          {
            campaign_id: campaignId,
            ad_group_id: firstLineItemId,
            account: facebookAdAccountId,
            detail: true,
            date_preset: 'lifetime'
          },
          FACEBOOK_PLATFORM
        )
      )
    }
  }, [dispatch, campaignId, firstLineItemId, facebookAdAccountId, shouldLoadAds])

  return {
    ...(campaignObjective === OUTCOME_TRAFFIC ? getOutcomeTrafficValues() : defaultValues),
    isDataLoading
  }
}
